import { isDefined } from "@clipboard-health/util-ts";
import { TaxSeasonBannerContainer } from "@src/appV2/Accounts/TaxInformation/components/TaxSeasonBannerContainer";
import { WorkerReferralBanner } from "@src/appV2/Accounts/WorkerReferrals/components/WorkerReferralBanner";
import { WorkplaceReferralsBanner } from "@src/appV2/Accounts/WorkplaceReferrals/components/WorkplaceReferralsBanner";
import { OrientationBanner } from "@src/appV2/ClipboardOrientation/components/OrientationBanner";
import { CbhFeatureFlag, useCbhFlag, useCbhFlags } from "@src/appV2/FeatureFlags";
import { FailedPayoutBannerContainer } from "@src/appV2/redesign/Payments/FailedPayouts/components/FailedPayoutBannerContainer";
import { type TierType } from "@src/appV2/redesign/Tiers/api/schema";
import { TiersBanner } from "@src/appV2/redesign/Tiers/TiersBanner";
import { useMaxReferralBonusAmount } from "@src/appV2/Referral/Bonus/flags";
import { useFlexActivationMessaging } from "@src/appV2/Referral/hooks/useFlexActivationMessaging";
import type React from "react";

export type BannerName =
  | "orientation"
  | "assessment"
  | "tiers"
  | "career_agent"
  | "workplace_referrals"
  | "worker_referral"
  | "workplace_and_worker_referrals"
  | "tax_season"
  | "failed_payout";

export interface BannerProps {
  workerId: string;
  orientationBannerText?: string;
  isOrientationBannerTextLoading: boolean;
  assessmentsBannerText?: string;
  isTiersEnabled: boolean;
  workerTier: TierType;
  bannersToSkip?: BannerName[];
}

interface AccountPageBannerProps {
  bannerName: BannerName;
  shouldRender: boolean;
  children: React.ReactElement;
  bannersToSkip?: BannerName[];
}

function AccountPageBanner({
  bannerName,
  shouldRender,
  bannersToSkip = [],
  children,
}: AccountPageBannerProps) {
  if (!shouldRender || bannersToSkip.includes(bannerName)) {
    return null;
  }

  return children;
}

function OrientationBannerWrapper(props: BannerProps) {
  const { orientationBannerText, isOrientationBannerTextLoading } = props;

  const shouldRender = !isOrientationBannerTextLoading && isDefined(orientationBannerText);

  return (
    <AccountPageBanner bannerName="orientation" shouldRender={shouldRender} {...props}>
      <OrientationBanner title={orientationBannerText!} />
    </AccountPageBanner>
  );
}

function WorkplaceReferralsBannerWrapper(props: BannerProps) {
  const { orientationBannerText, isOrientationBannerTextLoading, assessmentsBannerText } = props;
  const workplaceReferralsConfig = useCbhFlag(CbhFeatureFlag.WORKPLACE_REFERRALS, {
    defaultValue: {},
  });
  const isWorkerToWorkplaceReferralsEnabled = workplaceReferralsConfig.workerToWorkplace ?? false;

  const shouldRender =
    !isOrientationBannerTextLoading &&
    !isDefined(orientationBannerText) &&
    !isDefined(assessmentsBannerText) &&
    isWorkerToWorkplaceReferralsEnabled;

  return (
    <AccountPageBanner bannerName="workplace_referrals" shouldRender={shouldRender} {...props}>
      <WorkplaceReferralsBanner />
    </AccountPageBanner>
  );
}

function WorkerReferralBannerWrapper(props: BannerProps) {
  const { orientationBannerText, isOrientationBannerTextLoading, assessmentsBannerText } = props;

  const bonusAmount = useMaxReferralBonusAmount("postOnboardingBonusInDollars");
  const { [CbhFeatureFlag.ENABLED_REFERRAL_PROGRAMS]: enabledReferralPrograms } = useCbhFlags();

  const { getReferralRequirementMessage } = useFlexActivationMessaging();

  const activationBasedReferralIsEnabled =
    enabledReferralPrograms?.activationReferralIsEnabled ?? true;

  const shouldRender =
    activationBasedReferralIsEnabled &&
    !isOrientationBannerTextLoading &&
    !isDefined(orientationBannerText) &&
    !isDefined(assessmentsBannerText);

  return (
    <AccountPageBanner bannerName="worker_referral" shouldRender={shouldRender} {...props}>
      <WorkerReferralBanner
        bonusAmount={bonusAmount}
        getReferralRequirementMessage={getReferralRequirementMessage}
      />
    </AccountPageBanner>
  );
}

function TiersBannerWrapper(props: BannerProps) {
  const { isTiersEnabled } = props;
  const shouldRender = isTiersEnabled;

  return (
    <AccountPageBanner bannerName="tiers" shouldRender={shouldRender} {...props}>
      <TiersBanner {...props} />
    </AccountPageBanner>
  );
}

function FailedPayoutBannerWrapper(props: BannerProps) {
  return (
    <AccountPageBanner shouldRender bannerName="failed_payout" {...props}>
      <FailedPayoutBannerContainer sourceUI="account-tab" />
    </AccountPageBanner>
  );
}

function TaxSeasonBannerWrapper(props: BannerProps) {
  const shouldRender = true; // Banner handles its own conditional rendering internally

  return (
    <AccountPageBanner bannerName="tax_season" shouldRender={shouldRender} {...props}>
      <TaxSeasonBannerContainer sourceUI="account-tab" />
    </AccountPageBanner>
  );
}

export const BANNERS: Array<React.ComponentType<BannerProps>> = [
  FailedPayoutBannerWrapper,
  TaxSeasonBannerWrapper,
  TiersBannerWrapper,
  OrientationBannerWrapper,
  WorkerReferralBannerWrapper,
  WorkplaceReferralsBannerWrapper,
];
